import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  Calendar,
  MapPin,
  Clock,
  Users,
  ExternalLink,
  ChevronDown,
  Star,
  Plus,
  DollarSign,
  Tag,
  ChevronRight,
  Filter,
  X
} from 'lucide-react'
import {
  SAMPLE_EVENTS,
  EVENT_TYPES,
  getUpcomingEvents,
  getFeaturedEvents,
  formatEventDate
} from '../../lib/eventsData'

const EventCard = ({ event, compact = false }) => {
  const eventDate = new Date(event.date + 'T00:00:00')
  const isPast = eventDate < new Date()

  if (compact) {
    return (
      <div className={`flex items-center gap-4 p-4 bg-white rounded-xl border border-rdc-stone/30 hover:border-rdc-stone transition-colors ${isPast ? 'opacity-60' : ''}`}>
        <div className="flex-shrink-0 w-14 text-center">
          <div className="text-xs font-semibold text-rdc-orange uppercase">
            {eventDate.toLocaleDateString('en-US', { month: 'short' })}
          </div>
          <div className="text-2xl font-bold text-black">
            {eventDate.getDate()}
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-medium text-black truncate">{event.title}</h3>
          <div className="flex items-center gap-2 text-xs text-rdc-taupe mt-0.5">
            <MapPin size={11} />
            <span className="truncate">{event.location}</span>
          </div>
        </div>
        <ChevronRight size={18} className="text-rdc-stone" />
      </div>
    )
  }

  return (
    <div className={`card overflow-hidden ${event.featured ? 'ring-1 ring-rdc-orange/20' : ''} ${isPast ? 'opacity-60' : ''}`}>
      {/* Image */}
      {event.image && (
        <div className="img-container aspect-card relative">
          <img
            src={event.image}
            alt={event.title}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
          {event.featured && (
            <span className="absolute top-3 right-3 text-xs font-semibold text-white bg-rdc-orange/90 px-2.5 py-1 rounded-full">
              Featured
            </span>
          )}
        </div>
      )}

      <div className="p-5">
        {/* Date & Type */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1.5 text-sm font-medium text-rdc-orange">
            <Calendar size={14} />
            {formatEventDate(event.date, event.endDate)}
          </div>
          <span className="text-xs px-2 py-0.5 bg-rdc-cream text-rdc-charcoal rounded-full capitalize">
            {event.type}
          </span>
        </div>

        {/* Title */}
        <h3 className="section-title text-base mb-2">{event.title}</h3>

        {/* Location & Time */}
        <div className="flex flex-col gap-1.5 mb-3 text-sm text-rdc-charcoal">
          <div className="flex items-center gap-2">
            <MapPin size={14} className="text-rdc-taupe flex-shrink-0" />
            <span className="truncate">{event.location}</span>
          </div>
          {event.time && (
            <div className="flex items-center gap-2">
              <Clock size={14} className="text-rdc-taupe flex-shrink-0" />
              <span>{event.time}</span>
            </div>
          )}
        </div>

        {/* Description */}
        <p className="text-sm text-rdc-charcoal mb-4 line-clamp-2">{event.description}</p>

        {/* Tags */}
        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {event.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="text-xs px-2 py-0.5 bg-rdc-orange/8 text-rdc-orange rounded-full"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-4 border-t border-rdc-stone/30">
          <div className="flex items-center gap-4 text-sm text-rdc-taupe">
            {event.cost && (
              <div className="flex items-center gap-1">
                <DollarSign size={14} />
                <span>{event.cost}</span>
              </div>
            )}
            {event.rsvpCount && (
              <div className="flex items-center gap-1">
                <Users size={14} />
                <span>{event.rsvpCount} interested</span>
              </div>
            )}
          </div>
          {event.link && (
            <a
              href={event.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-rdc-orange font-medium hover:underline"
            >
              Details
              <ExternalLink size={12} />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default function Events() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState('all')
  const [showFilters, setShowFilters] = useState(false)

  // Filter events
  const getFilteredEvents = () => {
    let events = [...SAMPLE_EVENTS]

    // Sort by date
    events.sort((a, b) => new Date(a.date) - new Date(b.date))

    if (selectedType !== 'all') {
      events = events.filter(e => e.type === selectedType)
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      events = events.filter(e =>
        e.title.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.tags?.some(t => t.toLowerCase().includes(q))
      )
    }

    return events
  }

  const filteredEvents = getFilteredEvents()
  const today = new Date().toISOString().split('T')[0]
  const upcomingEvents = filteredEvents.filter(e => e.date >= today)
  const pastEvents = filteredEvents.filter(e => e.date < today)
  const featuredEvents = upcomingEvents.filter(e => e.featured)

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedType('all')
  }

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-label mb-1">Calendar</p>
          <h1 className="page-title">Upcoming Events</h1>
          <p className="text-body mt-2">
            Car shows, rallies, track days, and more
          </p>
        </div>
        <button className="btn-primary">
          <Plus size={18} />
          Submit Event
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-rdc-taupe" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events by name, location, or tags..."
            className="input-field pl-11"
          />
        </div>
      </div>

      {/* Active Filters */}
      {selectedType !== 'all' && (
        <div className="flex flex-wrap items-center gap-2 mb-5">
          <span className="text-xs text-rdc-taupe">Active filters:</span>
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 bg-rdc-orange/10 text-rdc-orange rounded-full">
            {EVENT_TYPES.find(t => t.id === selectedType)?.label}
            <button onClick={() => setSelectedType('all')} className="hover:text-rdc-orange/70">
              <X size={12} />
            </button>
          </span>
          <button
            onClick={clearFilters}
            className="text-xs text-rdc-taupe hover:text-rdc-charcoal underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Type Pills */}
      <div className="flex gap-2 overflow-x-auto pb-4 mb-6 -mx-2 px-2 scrollbar-hide">
        {EVENT_TYPES.map(type => (
          <button
            key={type.id}
            onClick={() => setSelectedType(type.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
              selectedType === type.id
                ? 'bg-rdc-orange text-white shadow-sm'
                : 'bg-white text-rdc-charcoal hover:bg-rdc-cream border border-rdc-stone/50'
            }`}
          >
            {type.label}
          </button>
        ))}
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-rdc-taupe">
          {upcomingEvents.length} upcoming event{upcomingEvents.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Results */}
      {upcomingEvents.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-rdc-cream flex items-center justify-center">
            <Calendar size={28} className="text-rdc-taupe" strokeWidth={1.5} />
          </div>
          <h3 className="section-title mb-2">No events found</h3>
          <p className="text-body text-rdc-charcoal mb-6">
            Try adjusting your search or check back soon
          </p>
          <button onClick={clearFilters} className="btn-secondary">
            Clear All Filters
          </button>
        </div>
      ) : (
        <>
          {/* Featured Events */}
          {featuredEvents.length > 0 && (
            <div className="mb-10">
              <h2 className="flex items-center gap-2 text-label mb-4">
                <Star size={14} className="text-rdc-orange" />
                Featured Events
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {featuredEvents.slice(0, 4).map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}

          {/* All Upcoming Events */}
          <div className="mb-10">
            <h2 className="text-label mb-4">All Upcoming Events</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {upcomingEvents.filter(e => !e.featured).map(event => (
                <EventCard key={event.id} event={event} />
              ))}
            </div>
          </div>

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div className="mt-12">
              <h2 className="text-label mb-4 text-rdc-taupe">Past Events</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pastEvents.slice(0, 4).map(event => (
                  <EventCard key={event.id} event={event} compact />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Submit Event CTA */}
      <div className="mt-12 card p-8 bg-gradient-to-br from-rdc-cream via-white to-rdc-cream text-center">
        <h3 className="section-title mb-2">Hosting an event?</h3>
        <p className="text-body text-rdc-charcoal mb-5">
          Share your car show, drive, or meetup with the community
        </p>
        <button className="btn-primary">
          <Plus size={16} />
          Submit an Event
        </button>
      </div>
    </div>
  )
}
